import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left.js';
import ArrowUpRight from 'lucide-react/dist/esm/icons/arrow-up-right.js';
import MessageCircle from 'lucide-react/dist/esm/icons/message-circle.js';
import { Button } from '@/components/ui/button';
import { products, pricingRules } from '@/data/products';
import { headwearLandingPageMap } from '@/data/headwearLandingPages';
import { headwearContent } from '@/data/headwearContent';
import { resolveSurfacedProducts } from '@/lib/headwearLandingProducts';
import { getProductSideImage } from '@/lib/productImages';
import { getCapPresentationViews, capImageSrc, capImageSrcSet } from '@/data/capPresentation';

import PrintedCapsLanding from '@/components/PrintedCapsLanding';
import HeadwearDiscovery from '@/components/HeadwearDiscovery';

function formatILS(value) {
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency: 'ILS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function fromPrice(product) {
  return (pricingRules[product.sku]?.tiers || [])
    .filter((tier) => Number.isFinite(Number(tier.price)))
    .reduce((lowest, tier) => !lowest || Number(tier.price) < Number(lowest.price) ? tier : lowest, null);
}

function buildProductMap() {
  return Object.fromEntries(products.map((product) => [product.sku, product]));
}

function LandingNotFound() {
  return (
    <div className="min-h-screen bg-white px-4 py-20">
      <div className="mx-auto max-w-3xl rounded-[32px] border border-gray-200 bg-gray-50 px-8 py-12 text-right">
        <p className="mb-3 text-sm font-semibold text-blue-700">כיסויי ראש / עמוד לא נמצא</p>
        <h1 className="mb-4 text-4xl font-normal tracking-tight text-gray-900">
          הדף שחיפשתם כבר לא נמצא במסלול הזה
        </h1>
        <p className="mb-8 max-w-2xl text-lg leading-8 text-gray-600">
          אפשר להמשיך לקטלוג כובעים וכיפות, או לעבור ישירות לעמוד שמסביר את שיטות המיתוג.
        </p>
        <div className="flex flex-wrap justify-end gap-3">
          <Link to="/catalog?filter=headwear">
            <Button size="lg" className="rounded-full px-7">
              לקטלוג כובעים וכיפות
            </Button>
          </Link>
          <Link to="/methods-of-branding">
            <Button size="lg" variant="outline" className="rounded-full px-7">
              לשיטות המיתוג
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function HeroProduct({ product, index, theme }) {
  const rotation = [-8, 4, -3, 6][index % 4];
  const [primaryView] = getCapPresentationViews(product);
  const imageSrc = primaryView ? capImageSrc(primaryView.image) : getProductSideImage(product, 'front', [], { usePlainFrontWhenNoColor: true });
  return (
    <motion.div
      initial={{ opacity: 0, y: 40, rotate: rotation - 4 }}
      animate={{ opacity: 1, y: 0, rotate: rotation }}
      transition={{ duration: 0.7, delay: 0.08 * index }}
      className="relative overflow-hidden rounded-[28px] border border-white/20 bg-white/90 p-3 shadow-[0_20px_80px_rgba(15,23,42,0.18)] backdrop-blur"
    >
      <div
        className="absolute inset-0 opacity-70"
        style={{
          background: `radial-gradient(circle at top right, ${theme.muted} 0%, rgba(255,255,255,0) 65%)`,
        }}
      />
      <img
        src={imageSrc}
        srcSet={primaryView ? capImageSrcSet(primaryView.image) : undefined}
        sizes="(min-width: 640px) 320px, 60vw"
        alt={primaryView?.alt || product.nameHe || product.name}
        className="relative h-52 w-full object-contain sm:h-64"
        loading="eager"
      />
      <div className="relative mt-2 text-right">
        <div className="text-sm font-semibold text-gray-900">{product.nameHe || product.name}</div>
        <div className="text-xs text-gray-500">SKU {product.sku}</div>
      </div>
    </motion.div>
  );
}

function ProductCard({ product, reason, theme }) {
  const [primaryView] = getCapPresentationViews(product);
  const imageSrc = primaryView ? capImageSrc(primaryView.image) : getProductSideImage(product, 'front', [], { usePlainFrontWhenNoColor: true });
  const cheapestTier = fromPrice(product);
  return (
    <article className="group overflow-hidden rounded-[28px] bg-white shadow-[0_12px_50px_rgba(15,23,42,0.08)] ring-1 ring-black/5">
      <div
        className="relative overflow-hidden px-4 pt-5"
        style={{
          background: `linear-gradient(180deg, ${theme.surface} 0%, rgba(255,255,255,1) 100%)`,
        }}
      >
        <img
          src={imageSrc}
          srcSet={primaryView ? capImageSrcSet(primaryView.image) : undefined}
          sizes="(min-width: 1024px) 30vw, (min-width: 640px) 50vw, 100vw"
          alt={primaryView?.alt || product.nameHe || product.name}
          className="mx-auto h-56 w-full object-contain transition-transform duration-300 group-hover:scale-[1.03]"
          loading="lazy"
        />
      </div>
      <div className="space-y-4 px-5 pb-5 text-right">
        <div className="flex items-start justify-between gap-4">
          <div className="rounded-full px-3 py-1 text-xs font-semibold text-gray-700 ring-1 ring-black/10">
            {product.sku}
          </div>
          <div>
            <h3 className="text-xl font-medium text-gray-900">{product.nameHe || product.name}</h3>
            <p className="mt-1 text-sm text-gray-500">{cheapestTier == null ? 'מחיר בדף המוצר' : `החל מ-${formatILS(cheapestTier.price)} ליחידה (${cheapestTier.min}+ יח׳), כולל מע״מ`}</p>
          </div>
        </div>
        <p className="text-sm leading-7 text-gray-600">{reason}</p>
        <div className="flex flex-wrap justify-end gap-3 text-sm">
          <Link
            to={`/product/${product.sku}`}
            className="inline-flex items-center gap-2 font-semibold text-gray-900"
          >
            לעמוד המוצר
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function HeadwearLandingPage() {
  const { landingKey } = useParams();
  const landing = landingKey ? headwearLandingPageMap[landingKey] : null;
  const content = landing ? headwearContent[landing.key] : null;

  const productMap = useMemo(buildProductMap, []);
  const featuredProducts = useMemo(
    () => (landing?.featuredSkus || []).map((sku) => productMap[sku]).filter(Boolean),
    [landing, productMap]
  );
  const surfacedProducts = useMemo(
    () => resolveSurfacedProducts(landing, productMap),
    [landing, productMap]
  );

  if (!landing) {
    return <LandingNotFound />;
  }

  if (landing.key === 'printed-caps') {
    return (
      <>
        <PrintedCapsLanding
          landing={landing}
          surfacedProducts={surfacedProducts}
        />
        <HeadwearDiscovery currentKey={landing.key} />
      </>
    );
  }

  const heroStyle = {
    background: `linear-gradient(135deg, ${landing.theme.heroStart} 0%, ${landing.theme.heroEnd} 100%)`,
  };

  return (
    <>
      <div className="min-h-screen bg-white text-right">
        <section className="relative overflow-hidden px-4 py-10 sm:px-6 lg:px-8 lg:py-14" style={heroStyle}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.22),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.12),transparent_24%)]" />
          <div className="relative mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div className="order-2 lg:order-1">
              <div className="grid gap-4 sm:grid-cols-3">
                {featuredProducts.map((product, index) => (
                  <HeroProduct key={product.sku} product={product} index={index} theme={landing.theme} />
                ))}
              </div>
            </div>
            <div className="order-1 text-right text-white lg:order-2">
              <p className="mb-4 text-sm font-semibold tracking-[0.28em] text-white/80">
                {landing.eyebrow}
              </p>
              <div className="relative">
                <div className="pointer-events-none absolute -top-12 right-0 text-[88px] font-semibold leading-none text-white/10 sm:text-[124px]">
                  {landing.backgroundWord}
                </div>
                <h1 className="relative max-w-2xl text-4xl font-normal leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                  {content.title}
                </h1>
              </div>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-white/86 sm:text-xl">
                {content.description}
              </p>
              <div className="mt-8 flex flex-wrap justify-end gap-3">
                <Link to={landing.primaryCta.to}>
                  <Button
                    size="lg"
                    className="rounded-full border-0 px-8 text-base font-semibold text-white"
                    style={{
                      background: `linear-gradient(90deg, ${landing.theme.accent} 0%, ${landing.theme.heroEnd} 100%)`,
                    }}
                  >
                    {landing.primaryCta.label}
                  </Button>
                </Link>
                <Link to={landing.secondaryCta.to}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-full border-white/35 bg-white/8 px-8 text-base font-semibold text-white hover:bg-white/12 hover:text-white"
                  >
                    {landing.secondaryCta.label}
                  </Button>
                </Link>
              </div>
              <div className="mt-5 flex items-center justify-end gap-2 text-sm text-white/82">
                <MessageCircle className="h-4 w-4" />
                <Link to="/contact" className="underline decoration-white/30 underline-offset-4">
                  צריכים כמות גדולה או התאמה נוספת? דברו איתנו.
                </Link>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-gray-100 bg-white px-4 py-10 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="text-sm font-semibold text-blue-700">לפני שבוחרים דגם</p>
                <h2 className="mt-2 text-3xl font-normal tracking-tight text-gray-900">
                  {content.sectionHeading}
                </h2>
              </div>
              <Link to={landing.catalogPath} className="inline-flex items-center gap-2 text-sm font-semibold text-gray-700">
                מעבר ישיר לקטלוג המסונן
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {content.spotlight.map((item, index) => (
                <motion.article
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.45, delay: index * 0.05 }}
                  className="rounded-[28px] border border-gray-100 px-6 py-6"
                  style={{ background: index === 1 ? landing.theme.surface : '#fff' }}
                >
                  <h3 className="mb-3 text-xl font-medium text-gray-900">{item.title}</h3>
                  <p className="text-sm leading-7 text-gray-600">{item.body}</p>
                </motion.article>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap justify-end gap-3">
              {content.heroPoints.map((point) => (
                <div
                  key={point}
                  className="rounded-full px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-black/10"
                  style={{ backgroundColor: landing.theme.muted }}
                >
                  {point}
                </div>
              ))}
            </div>

            {content.taxonomyNote ? (
              <div className="mt-8 rounded-[28px] px-6 py-5 text-sm leading-7 text-gray-700 ring-1 ring-black/10" style={{ backgroundColor: landing.theme.surface }}>
                {content.taxonomyNote}
              </div>
            ) : null}
          </div>
        </section>

        <section className="bg-[#fbfbfc] px-4 py-14 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="mb-8 flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="text-sm font-semibold text-blue-700">הדגמים שמופיעים בדף הזה</p>
                <h2 className="mt-2 text-3xl font-normal tracking-tight text-gray-900">
                  בוחרים דגם ובודקים את הפרטים שלו
                </h2>
              </div>
              <Link to={landing.catalogPath}>
                <Button size="lg" variant="outline" className="rounded-full px-7">
                  לכל הדגמים במסלול הזה
                </Button>
              </Link>
            </div>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {surfacedProducts.map((product) => (
                <ProductCard
                  key={product.sku}
                  product={product}
                  reason={product.descriptionHe || product.description}
                  theme={landing.theme}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-14 sm:px-6 lg:px-8">
          <div
            className="mx-auto max-w-7xl overflow-hidden rounded-[32px] px-8 py-10 text-white"
            style={{ background: `linear-gradient(90deg, ${landing.theme.heroStart} 0%, ${landing.theme.heroEnd} 100%)` }}
          >
            <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
              <div className="space-y-3">
                <p className="text-sm font-semibold tracking-[0.22em] text-white/72">{landing.eyebrow}</p>
                <h2 className="text-3xl font-normal leading-tight sm:text-4xl">
                  מוכנים לבחור דגם ולהתקדם משם?
                </h2>
              </div>
              <div>
                <p className="max-w-2xl text-lg leading-8 text-white/84">
                  בחרו את הדגם המתאים ובדקו את הצבעים, הכמויות ואזורי המיתוג בדף המוצר. אם יש שאלה לגבי העיצוב או ההזמנה, אפשר לפנות אלינו לפני שממשיכים.
                </p>
                <div className="mt-7 flex flex-wrap justify-end gap-3">
                  <Link to={landing.finalCta.to}>
                    <Button
                      size="lg"
                      className="rounded-full border-0 bg-white px-8 text-base font-semibold text-gray-900 hover:bg-white/90"
                    >
                      {landing.finalCta.label}
                    </Button>
                  </Link>
                  <Link to={landing.finalSecondaryCta.to}>
                    <Button
                      size="lg"
                      variant="outline"
                      className="rounded-full border-white/35 bg-white/8 px-8 text-base font-semibold text-white hover:bg-white/12 hover:text-white"
                    >
                      {landing.finalSecondaryCta.label}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
        <HeadwearDiscovery currentKey={landing.key} />
      </div>
    </>
  );
}
